import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import type { ComponentType } from "react";
import Skeleton from "react-loading-skeleton";
import type { ContentfulArticle } from "../../common/types/cmsTypes";
import { LIST_TYPE } from "../../common/types/trackingTypes";
import { initializeWithDefaultData, isDefaultData, isErrorOccured, isWaitingForData } from "../../common/utils/asyncDataUtils";
import ProductList from "../../components/common/product/ProductList";
import ErrorAlertWithOptionalTracking from "../../components/ErrorAlertWithOptionalTracking";
import contentStore from "../../stores/cms/contentStore";
import segmentStore from "../../stores/cms/segmentStore";
import productStore from "../../stores/product/productStore";
import { hasDiscount } from "../../stores/product/productUtils";
import promotionStore from "../../stores/promotionStore";
import PromotionTeaser from "./PromotionTeaser";

export const PROMOTION_SECTION_SLUG = "kampanjer";

const PromotionPage: ComponentType = () => {
   const promotionId = contentStore.getSectionIdFromSlug(PROMOTION_SECTION_SLUG);

   if (!promotionId) {
      return (
         <>
            <h1 className="mb-2">Kampanjer</h1>
            <Skeleton height={380} />
         </>
      );
   }
   // TODO: is it intentional that we update state here, in the render body?
   if (!contentStore.sectionContent[promotionId]) {
      runInAction(() => {
         contentStore.sectionContent[promotionId] = initializeWithDefaultData([]);
      });
   }

   const allPromotionSectionContent = contentStore.sectionContent[promotionId];
   if (isDefaultData(allPromotionSectionContent)) {
      void contentStore.fetchSectionContentForId(promotionId);
   }

   if (isWaitingForData(allPromotionSectionContent)) {
      return (
         <>
            <h1 className="mb-2">Kampanjer</h1>
            <Skeleton height={380} />
         </>
      );
   }

   if (isErrorOccured(allPromotionSectionContent)) {
      return (
         <ErrorAlertWithOptionalTracking
            showCloseButton={false}
            gaErrorTracking={{ context: "promotions", text: "page_load_error" }}
         >
            Det oppsto en uventet feil under lasting av siden
         </ErrorAlertWithOptionalTracking>
      );
   }

   const promotionArticles = allPromotionSectionContent.data.filter((object) => {
      return object.sys.contentType.sys.id === "v2_article" && segmentStore.isArticleVisible(object.sys.id);
   });
   const allProductsInAssortment = productStore.products;
   const productsWithDiscount = allProductsInAssortment.filter((product) => {
      return hasDiscount(product) || promotionStore.getPromotionsBySku(product.sku).length > 0;
   });

   for (const promotionArticle of promotionArticles) {
      contentStore.addViewedPromotionArticleId(promotionArticle.sys.id);
   }

   return (
      <>
         <h1 className="mb-2">Kampanjer</h1>
         {promotionArticles.length === 0 && productsWithDiscount.length === 0 && <p>Ingen kampanjer tilgjengelig</p>}
         {promotionArticles.map((object, i) => {
            const article = object as ContentfulArticle;
            const imagePosition = i % 2 === 0 ? "Left" : "Right";
            return (
               <PromotionTeaser
                  key={`promotion-article-${article.fields.title}`}
                  article={article}
                  imagePosition={imagePosition}
               />
            );
         })}
         {productsWithDiscount.length > 0 && (
            <div className="mt-8">
               <h2 className="mb-2">Kampanjeprodukter</h2>
               <ProductList
                  products={productsWithDiscount}
                  listName="Kampanjeprodukter"
                  listType={LIST_TYPE.CUSTOM}
                  context="discounted-products"
               />
            </div>
         )}
      </>
   );
};

export default observer(PromotionPage);
